import { api, http } from '@/api/client'
import type { StoredFile } from '@/features/students/types'
import type { VideoState } from './types'

/** Files above this size, and every video, go straight to storage instead of through the API. */
export const DIRECT_UPLOAD_THRESHOLD = 20 * 1024 * 1024

export function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/')
}

type Progress = (fraction: number) => void

interface DirectPlan {
  id: string
  partSize: number
  parts: { number: number; url: string }[]
}

/** PUT one part to its signed storage link (no session token: the link carries the permission). */
function putPart(
  url: string,
  blob: Blob,
  onProgress: (loaded: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.upload.onprogress = (e) => onProgress(e.loaded)
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`))
    xhr.onerror = () => reject(new Error('network'))
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'))
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(blob)
  })
}

async function withRetries<T>(attempts: number, fn: () => Promise<T>, signal?: AbortSignal): Promise<T> {
  let last: unknown
  for (let i = 0; i < attempts; i++) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
    try {
      return await fn()
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e
      last = e
      await new Promise((r) => setTimeout(r, 1000 * 2 ** i))
    }
  }
  throw last
}

/**
 * A large file (or a video to download) sent in parallel 16 MB parts straight to storage (R2 / S3 /
 * MinIO), then assembled and checked by the API. Returns the stored file to attach.
 */
export async function uploadDirect(
  file: File,
  onProgress: Progress,
  signal?: AbortSignal,
): Promise<StoredFile> {
  const plan = await api.post<DirectPlan>('/lms/uploads', {
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
  })
  const loaded = new Map<number, number>()
  const report = () => onProgress([...loaded.values()].reduce((a, b) => a + b, 0) / file.size)
  const queue = [...plan.parts]
  const worker = async () => {
    for (let part = queue.shift(); part; part = queue.shift()) {
      const p = part
      const blob = file.slice((p.number - 1) * plan.partSize, p.number * plan.partSize)
      await withRetries(
        4,
        () =>
          putPart(
            p.url,
            blob,
            (n) => {
              loaded.set(p.number, n)
              report()
            },
            signal,
          ),
        signal,
      )
    }
  }
  try {
    await Promise.all([worker(), worker(), worker()])
    return await api.post<StoredFile>(`/lms/uploads/${plan.id}/complete`)
  } catch (e) {
    void api.delete(`/lms/uploads/${plan.id}`).catch(() => undefined)
    throw e
  }
}

/**
 * A video to stream: sent to the server in 8 MB chunks (resumed from the server's offset after a
 * failure), then converted by FFmpeg on the server. Resolves once the conversion is queued.
 */
export async function uploadStreamingVideo(
  file: File,
  onProgress: Progress,
  signal?: AbortSignal,
): Promise<VideoState> {
  const video = await api.post<VideoState & { chunkSize: number }>('/lms/videos', {
    name: file.name,
    mimeType: file.type,
    size: file.size,
  })
  let offset = 0
  let failures = 0
  while (offset < file.size) {
    if (signal?.aborted) {
      void api.delete(`/lms/videos/${video.id}`).catch(() => undefined)
      throw new DOMException('Aborted', 'AbortError')
    }
    const chunk = file.slice(offset, offset + video.chunkSize)
    try {
      const res = await http.put<{ data: { received: number } }>(`/lms/videos/${video.id}/chunks`, chunk, {
        params: { offset },
        headers: { 'Content-Type': 'application/octet-stream' },
        signal,
        onUploadProgress: (e) => onProgress((offset + e.loaded) / file.size),
      })
      offset = res.data.data.received
      failures = 0
    } catch (e) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
      if (++failures > 5) throw e
      await new Promise((r) => setTimeout(r, 1000 * 2 ** failures))
      // Ask the server where it stopped and continue from there.
      offset = (await api.get<VideoState>(`/lms/videos/${video.id}`)).received
    }
    onProgress(offset / file.size)
  }
  return api.post<VideoState>(`/lms/videos/${video.id}/complete`)
}

/** Opens a signed download link (the browser downloads straight from storage). */
export async function downloadLmsFile(fileId: number): Promise<void> {
  const { url } = await api.get<{ url: string }>(`/lms/files/${fileId}/link`)
  const a = document.createElement('a')
  a.href = url
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}
